import React from 'react';
import { Modal } from '@/components/ui';

// ResponsiveModal (quantified-tracking) — legacy wrapper kept for its existing call sites
// (TaskModal, H200TaskModal, the standing-task modals, …). It now delegates to the shared
// `ui/Modal` primitive instead of hand-rolling a `.modal-dialog` overlay.
//
// Why this changed: the old version emitted `mobile-fullscreen mobile-{orientation}` on
// phones — class names with no CSS definition anywhere in the repo — so on a phone these
// modals lost all sizing and rendered unstyled. Delegating also gains the focus trap,
// body-scroll lock, ESC handling, pinned header/footer and transitions.
// Prefer using `Modal` directly in new code.
interface ResponsiveModalProps {
  /**
   * V2-074 / EDIT-01: show the form read-only – values as label / value text, with no input
   * boxes, borders, drop-down arrows or required asterisks (`.wrap-readonly-form` in
   * tailwind.css). For a locked task or a View-only record.
   */
  readOnly?: boolean;
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  footer?: React.ReactNode;
  /** Default '2xl'. The Edit Task dialog passes '3xl' so its 12-column Efforts table fits (W-M8). */
  size?: '2xl' | '3xl';
  /** 'fullscreen' = full-screen sheet on a tablet (W-iP10). Default: centred card. */
  tabletVariant?: 'fullscreen' | 'center';
}

const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
  children,
  isOpen,
  onClose,
  readOnly = false,
  title,
  footer,
  size = '2xl',
  tabletVariant,
}) => (
  // '2xl' ≈ the old `.standardized-modal` 1200px desktop width.
  <Modal show={isOpen} onHide={onClose} size={size} tabletVariant={tabletVariant}>
    <Modal.Header closeButton>
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body className={readOnly ? 'wrap-readonly-form' : ''}>{children}</Modal.Body>
    {footer && <Modal.Footer>{footer}</Modal.Footer>}
  </Modal>
);

export default ResponsiveModal;
